import { useEffect, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';

interface ScrollInfo {
  top: number;
  height: number;
  scrollHeight: number;
  atBottom: boolean;
}

/**
 * 스크롤 컨테이너의 스크롤을 rAF 로 스로틀해서 알려준다.
 *
 * 구 프로젝트의 훅은 onScroll 을 의존성에 넣어, 매 렌더마다 새로 만들어지는
 * 콜백 때문에 리스너를 계속 떼었다 붙였다 했다. 콜백은 ref 로 들고 간다.
 */
export function useScroller(
  ref: RefObject<HTMLElement | null>,
  onScroll: (info: ScrollInfo) => void,
  /** 바닥으로 인정할 여유 픽셀 */
  threshold = 80
) {
  const handler = useRef(onScroll);
  handler.current = onScroll;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ticking = false;
    const listener = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;

        const top = el.scrollTop;
        const height = el.clientHeight;
        const scrollHeight = el.scrollHeight;

        handler.current({
          top,
          height,
          scrollHeight,
          atBottom: top > 0 && top + height >= scrollHeight - threshold,
        });
      });
    };

    el.addEventListener('scroll', listener, { passive: true });
    listener();

    return () => el.removeEventListener('scroll', listener);
  }, [ref, threshold]);
}
